import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type QueryClient } from '@tanstack/react-query';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';
import { useSaveFileMutation } from '#apps/client-web/features/file-storage/hooks/use-save-file-mutation';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';
import { useDeleteFileMutation } from '#apps/client-web/features/file-storage/hooks/use-delete-file-mutation';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  AuditWindowsFormSchema,
  type AuditWindowsForm,
  type AuditWindowsFormEntry,
} from '#packages/data-collection/models/audit-windows-form';
import { type Attachment } from '#packages/data-collection/models/common/attachment';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { useMenu } from '#packages/web/hooks/use-menu';

import { fromFormData, toFormData } from './mappers';
import {
  EditAuditFormWindowsEntryDataSchema,
  type EditAuditFormWindowsEntryData,
} from './types';

export type AuditFormWindowsEntryPageViewModel = ReturnType<
  typeof useAuditFormWindowsEntryPageViewModel
>;

type AuditFormWindowsEntryPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormWindowsEntryPageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormWindowsEntryPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId, index: indexAsString } = useParams<{
    auditId: AuditId;
    index: string;
  }>();
  const index = parseInt(indexAsString!);

  const auditFormQuery = useLoadAuditFormByType<AuditWindowsForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'windows',
    queryClient,
  });

  const saveFileStorageMutation = useSaveFileMutation({
    fileStorageService,
    queryClient,
  });

  const deleteFileStorageMutation = useDeleteFileMutation({
    fileStorageService,
    queryClient,
  });

  const fileIds = useMemo(
    () =>
      (auditFormQuery.data?.entries[index]?.attachments ?? []).map(
        (item) => item.name,
      ),
    [auditFormQuery.data?.entries],
  );

  const fileStorageQuery = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditWindowsForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'windows',
  });

  const form = useForm({
    resolver: zodResolver(
      EditAuditFormWindowsEntryDataSchema,
    ) as Resolver<EditAuditFormWindowsEntryData>,
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  // Auto-calculate area from measurement fields
  const [
    isUsingMetricsVal,
    quantityVal,
    widthInFeetVal,
    widthInInchesVal,
    heightInFeetVal,
    heightInInchesVal,
    widthInMetersVal,
    heightInMetersVal,
  ] = form.watch([
    'isUsingMetrics',
    'quantity',
    'widthInFeet',
    'widthInInches',
    'heightInFeet',
    'heightInInches',
    'widthInMeters',
    'heightInMeters',
  ]);

  useEffect(() => {
    const qty = parseFloat(String(quantityVal ?? ''));
    if (isNaN(qty) || qty <= 0) {
      form.setValue('area', undefined, { shouldDirty: false });
      return;
    }

    let area: number | undefined;
    if (isUsingMetricsVal) {
      const w = parseFloat(String(widthInMetersVal ?? ''));
      const h = parseFloat(String(heightInMetersVal ?? ''));
      if (!isNaN(w) && !isNaN(h)) {
        area = qty * w * h;
      }
    } else {
      const wFt = parseFloat(String(widthInFeetVal ?? '')) || 0;
      const wIn = parseFloat(String(widthInInchesVal ?? '')) || 0;
      const hFt = parseFloat(String(heightInFeetVal ?? '')) || 0;
      const hIn = parseFloat(String(heightInInchesVal ?? '')) || 0;
      const totalW = wFt + wIn / 12;
      const totalH = hFt + hIn / 12;
      if (totalW > 0 && totalH > 0) {
        area = qty * totalW * totalH;
      }
    }

    form.setValue(
      'area',
      area !== undefined ? parseFloat(area.toFixed(2)) : undefined,
      { shouldDirty: false },
    );
  }, [
    isUsingMetricsVal,
    quantityVal,
    widthInFeetVal,
    widthInInchesVal,
    heightInFeetVal,
    heightInInchesVal,
    widthInMetersVal,
    heightInMetersVal,
  ]);

  const onSave = useCallback(
    (formData: EditAuditFormWindowsEntryData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug(
          'Cannot update windows entry, page is not in success state',
        );
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating windows form data');

      const newFileIds = (formData.attachments ?? []).map(
        (attachment) => attachment.name,
      );
      const deletedFileIds = fileIds.filter(
        (fileId) => !newFileIds.includes(fileId),
      );
      deletedFileIds.forEach((fileId) => {
        deleteFileStorageMutation.mutate({ fileId });
      });

      const attachments: Attachment[] | undefined = formData.attachments?.map(
        (attachment) => {
          saveFileStorageMutation.mutate({
            fileId: attachment.name,
            content: attachment.file,
          });
          return {
            name: attachment.name,
            mimeType: attachment.file.type,
            isSelectedForReport: attachment.isSelectedForReport,
          };
        },
      );

      const previousDataEncoded = auditFormQuery.data
        ? AuditWindowsFormSchema.encode(auditFormQuery.data)
        : { type: 'windows' as const, entries: [] };

      const newEntryRaw = fromFormData(formData, attachments);

      if (index === -1) {
        previousDataEncoded.entries.push(newEntryRaw);
      } else {
        previousDataEncoded.entries[index] = newEntryRaw;
      }

      const auditWindowsForm =
        AuditWindowsFormSchema.decode(previousDataEncoded);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditWindowsForm },
        {
          onSuccess: (savedForm: AuditWindowsForm) => {
            logger?.debug(
              { auditId, savedForm },
              'Windows form updated successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('Windows form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update windows form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      index,
      auditId,
      saveFileStorageMutation.mutate,
      auditFormQuery.data,
      AuditWindowsFormSchema.encode,
      AuditWindowsFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (
      (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) &&
      (saveFileStorageMutation.isIdle || saveFileStorageMutation.isSuccess) &&
      (deleteFileStorageMutation.isIdle || deleteFileStorageMutation.isSuccess)
    ) {
      return { state: 'idle' };
    }

    if (
      saveAuditFormMutation.isPending ||
      saveFileStorageMutation.isPending ||
      deleteFileStorageMutation.isPending
    ) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    if (saveFileStorageMutation.isError) {
      return { state: 'error', error: saveFileStorageMutation.error };
    }

    if (deleteFileStorageMutation.isError) {
      return { state: 'error', error: deleteFileStorageMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
    saveFileStorageMutation.isIdle,
    saveFileStorageMutation.isPending,
    saveFileStorageMutation.isSuccess,
    saveFileStorageMutation.isError,
    saveFileStorageMutation.error,
    deleteFileStorageMutation.isIdle,
    deleteFileStorageMutation.isPending,
    deleteFileStorageMutation.isSuccess,
    deleteFileStorageMutation.isError,
    deleteFileStorageMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<{
      form: AuditWindowsFormEntry | undefined;
      files: (FileContent | undefined)[];
    }>
  >(() => {
    const areFilesLoaded =
      fileIds.length === 0 ||
      fileStorageQuery.every((query) => query.isSuccess);
    if (auditFormQuery.isSuccess && areFilesLoaded) {
      const files =
        fileStorageQuery.map((item) => item.data ?? undefined) ?? undefined;
      return {
        state: 'success',
        data: { form: auditFormQuery.data?.entries[index], files },
      };
    }

    const areFilesLoading =
      fileIds.length > 0 &&
      fileStorageQuery.some((query) => query.isLoading || query.isPending);
    if (
      auditFormQuery.isLoading ||
      auditFormQuery.isPending ||
      areFilesLoading
    ) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return { state: 'error', error: auditFormQuery.error };
    }

    const fileError = fileStorageQuery.find((query) => query.isError)?.error;
    if (!isNil(fileError)) {
      return { state: 'error', error: fileError };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
    fileStorageQuery,
    fileIds,
    index,
  ]);

  const mainMenu = useMenu();

  const deleteDialog = useDialog<AuditWindowsFormEntry>();
  const isDeleteDisabled =
    updateMutationState.state === 'pending' ||
    loadAuditFormState.state !== 'success' ||
    loadAuditFormState.data.form == null ||
    index < 0;

  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (isDeleteDisabled || isNil(auditFormQuery.data)) {
        logger?.debug(
          'Cannot delete windows entry, page is not in success state for an existing entry',
        );
        return;
      }

      mainMenu.close();

      if (!confirmed) {
        logger?.debug('Opening windows entry deletion confirmation dialog');
        deleteDialog.open(loadAuditFormState.data.form);
        return;
      }

      const updatedData = { ...auditFormQuery.data };
      updatedData.entries.splice(index, 1);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: updatedData },
        {
          onSuccess: (auditForm) => {
            logger?.debug(
              { auditId, auditForm },
              'Windows entry deleted successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [AUDIT_FORMS_QUERY_KEY, auditId, updatedData.type],
              })
              .then(() => {
                logger?.debug('Windows list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate windows list queries',
                );
                throw err;
              });
          },
          onError: (err) => {
            logger?.error({ err, auditId }, 'Failed to delete windows entry');
            throw err;
          },
        },
      );
    },
    [auditId, loadAuditFormState, saveAuditFormMutation],
  );

  // Sync form default values when audit data is loaded
  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { auditWindowsForm: auditFormQuery.data },
      'Audit windows form default values',
    );

    form.reset(
      toFormData(loadAuditFormState.data.form, loadAuditFormState.data.files),
    );
  }, [auditFormQuery.data?.entries, auditFormQuery.isSuccess]);

  return {
    mainMenu,

    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    onDelete,
    deleteDialog,
    isDeleteDisabled,

    auditId,
  };
};
